import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Box,
  Container,
  Typography,
  Stack,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { logo } from '../assets';
import { themeColors } from '../App';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { title: 'HOME', path: '/' },
    { title: 'PRODUCTS', path: '/products' },
    { title: 'INFRASTRUCTURE', path: '/infrastructure' },
    { title: 'ABOUT US', path: '/about' },
    { title: 'CONTACT US', path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAddressClick = () => {
    window.open('https://maps.google.com/?q=E-143+Sector-63,+Noida,+Delhi-NCR,+UP,+India+201301', '_blank');
  };

  const handlePhoneClick = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber.replace(/[^\d+]/g, '')}`;
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:office@thevibuti.com';
  };

  const TopBar = () => (
    <Box sx={{ bgcolor: '#f5f5f5', py: 2, borderBottom: '1px solid #e0e0e0' }}>
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 3,
          justifyContent: 'space-between' 
        }}>
          {/* Logo */}
          <Box 
            sx={{ flexShrink: 0, cursor: 'pointer' }} 
            onClick={() => navigate('/')}
          >
            <img src={logo} alt="Company Logo" style={{ height: '90px' }} />
          </Box>

          {/* Address and Contact Info */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 16,
            flex: 1,
            justifyContent: 'center'
          }}>
            {/* Address */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1.5,
                cursor: 'pointer',
                '&:hover': { color: themeColors.primary }
              }}
              onClick={handleAddressClick}
            >
              <LocationOnIcon sx={{ color: themeColors.primary, flexShrink: 0, mt: 0.5 }} />
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  E-143 Sector-63,
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Noida, Delhi-NCR,
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  UP, India 201301
                </Typography>
              </Stack>
            </Box>

            {/* Contact Info */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Phone Numbers */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <PhoneIcon sx={{ color: themeColors.primary, flexShrink: 0, mt: 0.5 }} />
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                    Sales & Service
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': { color: themeColors.primary }
                      }}
                    >
                      <span onClick={() => handlePhoneClick('+91-9999922657')}>+91-9999922657</span> / <span onClick={() => handlePhoneClick('+91-9667674397')}>9667674397</span>
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              
              {/* Email */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  cursor: 'pointer',
                  '&:hover': { color: themeColors.primary }
                }}
                onClick={handleEmailClick}
              >
                <EmailIcon sx={{ color: themeColors.primary, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  office@thevibuti.com
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Request Quote Button */}
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            sx={{
              backgroundColor: themeColors.primary,
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: '30px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              boxShadow: '0 4px 12px rgba(26, 71, 42, 0.15)',
              background: 'linear-gradient(45deg, #1a472a 30%, #4caf50 90%)',
              border: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(45deg, #15371f 30%, #3d8c40 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(26, 71, 42, 0.2)',
                textDecoration: 'none'
              }
            }}
          >
            Request a Quote
          </Button>
        </Box>
      </Container>
    </Box>
  );

  const drawer = (
    <Box sx={{ bgcolor: 'white', height: '100%', pt: 2 }}>
      <List>
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.title}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 2,
              bgcolor: location.pathname === item.path ? 'rgba(26, 71, 42, 0.1)' : 'transparent',
              borderLeft: location.pathname === item.path ? `4px solid ${themeColors.primary}` : '4px solid transparent',
              '&:hover': {
                bgcolor: 'rgba(26, 71, 42, 0.1)',
                borderLeft: `4px solid ${themeColors.primary}`
              }
            }}
          >
            <ListItemText 
              primary={item.title} 
              sx={{ 
                color: location.pathname === item.path ? themeColors.primary : '#333',
                '.MuiTypography-root': {
                  fontWeight: location.pathname === item.path ? 600 : 400
                }
              }} 
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {!isMobile?<TopBar />:""}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          '& .MuiButton-root': {
            boxShadow: 'none !important',
            '&:hover': {
              bgcolor: 'transparent !important',
              boxShadow: 'none !important'
            }
          }
        }}
      >
        <Container maxWidth="lg">
          <Toolbar 
            disableGutters 
            sx={{ 
              display: 'flex',
              justifyContent: 'center',
              minHeight: { xs: 48, sm: 56 }
            }}
          >
            {isMobile ? (
              <>
              <Box sx={{
                display:"flex",
                justifyContent:"space-between",
                alignItems:"center",
                width:"100%"
              }}>

              
               <img src={logo} alt="Company Logo" style={{ height: '60px' }} />
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ 
                    color: '#333',
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
                </Box>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                gap: 4,
                justifyContent: 'center'
              }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.title}
                    component={Link}
                    to={item.path}
                    disableRipple
                    sx={{
                      color: '#333',
                      position: 'relative',
                      textTransform: 'uppercase',
                      '&:hover': {
                        bgcolor: 'transparent'
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '2px',
                        bgcolor: themeColors.primary,
                        transform: location.pathname === item.path ? 'scaleX(1)' : 'scaleX(0)',
                        transition: 'transform 0.3s ease-in-out',
                        transformOrigin: 'center'
                      },
                      '&:hover::after': {
                        transform: 'scaleX(1)'
                      }
                    }}
                  >
                    {item.title}
                  </Button>
                ))}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240,
            bgcolor: 'white',
            '& .MuiListItem-root': {
              '&:hover': {
                bgcolor: 'transparent'
              }
            }
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
